import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Icon from '../components/Icon';
import ImageLightbox from '../components/ImageLightbox';
import { ImageThumb } from '../components/DetailParts';
import { useToast } from '../components/Toast';
import {
  Inquiry,
  InquiryStatus,
  InquiryStatusCounts,
  listInquiries,
  setInquiryStatus,
} from '../api/inquiries';
import { Listing, getListing } from '../api/listings';
import { extractApiError } from '../api/legalServices';

type Filter = 'all' | InquiryStatus;
const FILTERS: Filter[] = ['all', 'new', 'contacted', 'converted'];

const FILTER_LABEL: Record<Filter, string> = {
  all: 'All',
  new: 'New',
  contacted: 'Contacted',
  converted: 'Converted',
};

const STATUS_LABEL: Record<InquiryStatus, string> = {
  new: 'New',
  contacted: 'Contacted',
  converted: 'Converted',
};

const statusStyle = (s: InquiryStatus): React.CSSProperties => {
  if (s === 'new') return { background: 'var(--brand-bg)', color: 'var(--brand)' };
  if (s === 'contacted') return { background: 'rgba(5,150,105,.08)', color: 'var(--green)' };
  return { background: 'rgba(37,99,235,.08)', color: 'var(--blue)' };
};

const initials = (name: string): string =>
  name
    .split(' ')
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

const formatDate = (iso?: string): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const formatTime = (iso?: string): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  });
};

const propertyOf = (i: Inquiry): string =>
  i.listing?.title || i.listing?.address || 'Listing #' + i.listingId;

/** API host without the trailing /api — '' in production (API_BASE_URL=/api). */
const API_ORIGIN = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/api\/?$/, '');

/**
 * Photos are stored as absolute URLs captured at upload time (some against a
 * dev host), so keep only the /uploads/... path and serve it from this
 * environment's API host (same-origin in production); leave other URLs untouched.
 */
const photoUrl = (u: string): string => {
  if (!u) return '';
  const m = /\/uploads\/.*$/.exec(u);
  return m ? API_ORIGIN + m[0] : u;
};

const petPolicyLabel: Record<string, string> = {
  allowed: 'Pet Friendly',
  'not-allowed': 'No Pets',
  'cats-only': 'Cats Only',
  'dogs-only': 'Dogs Only',
};
const leaseTermLabel: Record<string, string> = {
  'month-to-month': 'Month-to-month',
  '6-months': '6 months',
  '1-year': '1 year',
  '2-years': '2 years',
};

const priceText = (price?: Listing['price'], type?: Listing['type']): string => {
  const n = typeof price === 'number' ? price : Number(price);
  if (!Number.isFinite(n)) return '—';
  const s = '$' + n.toLocaleString();
  return type === 'Rental' ? `${s}/mo` : s;
};

const dateText = (iso?: string | null): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const Fact: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div>
    <div
      className="font-semibold text-g400 uppercase mb-1"
      style={{ fontSize: 10.5, letterSpacing: '0.5px' }}
    >
      {label}
    </div>
    <div className="text-ink" style={{ fontSize: 13 }}>
      {value}
    </div>
  </div>
);

/** The selected inquiry's full listing, rendered inline in the detail panel. */
const ListingDetailsInline: React.FC<{
  listing: Listing | null;
  loading: boolean;
  fallbackTitle: string;
  /** Open a photo in the on-page viewer (same as the admin panel). */
  onOpenPhoto: (src: string, title: string) => void;
}> = ({ listing, loading, fallbackTitle, onOpenPhoto }) => {
  if (loading) {
    return (
      <div className="text-g500" style={{ fontSize: 13 }}>
        <Icon name="building-2" size={14} color="var(--g400)" /> Loading listing…
      </div>
    );
  }
  if (!listing) {
    return (
      <div className="text-g500" style={{ fontSize: 13 }}>
        {fallbackTitle} — full details unavailable.
      </div>
    );
  }

  const photos = (
    listing.photos && listing.photos.length
      ? listing.photos
      : listing.coverPhoto
      ? [listing.coverPhoto]
      : []
  ).map(photoUrl);
  const amenities = Array.isArray(listing.amenities) ? listing.amenities : [];

  return (
    <div
      style={{
        border: '1px solid var(--g100)',
        borderRadius: 10,
        padding: 14,
        background: 'var(--g50)',
      }}
    >
      {photos.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3.5">
          {photos.map((p, i) => (
            <ImageThumb
              key={i}
              src={p}
              alt={`${listing.title} ${i + 1}`}
              size={96}
              onOpen={() =>
                onOpenPhoto(
                  p,
                  photos.length > 1
                    ? `${listing.title} · Photo ${i + 1} of ${photos.length}`
                    : listing.title
                )
              }
            />
          ))}
        </div>
      )}

      <div className="font-bold text-ink" style={{ fontSize: 15 }}>
        {listing.title}
      </div>
      <div className="flex items-center gap-1 text-g500 mb-3" style={{ fontSize: 12.5 }}>
        <Icon name="map-pin" size={13} color="var(--g400)" /> {listing.address}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-3 gap-y-3">
        <Fact label="Price" value={priceText(listing.price, listing.type)} />
        <Fact label="Type" value={listing.type || '—'} />
        <Fact label="Status" value={<span className="capitalize">{listing.status}</span>} />
        <Fact label="Beds" value={listing.beds ?? '—'} />
        <Fact label="Baths" value={listing.baths ?? '—'} />
        <Fact label="Sqft" value={listing.sqft ? listing.sqft.toLocaleString() : '—'} />
        <Fact
          label="Lease term"
          value={listing.leaseTerm ? leaseTermLabel[listing.leaseTerm] || listing.leaseTerm : '—'}
        />
        <Fact label="Available" value={dateText(listing.availableFrom)} />
        <Fact
          label="Pets"
          value={listing.petPolicy ? petPolicyLabel[listing.petPolicy] || listing.petPolicy : '—'}
        />
      </div>

      <div className="mt-3.5">
        <div
          className="font-semibold text-g400 uppercase mb-1"
          style={{ fontSize: 10.5, letterSpacing: '0.5px' }}
        >
          Description
        </div>
        <div className="text-g700" style={{ fontSize: 13, whiteSpace: 'pre-wrap' }}>
          {listing.description || 'No description provided.'}
        </div>
      </div>

      {amenities.length > 0 && (
        <div className="mt-3.5">
          <div
            className="font-semibold text-g400 uppercase mb-1.5"
            style={{ fontSize: 10.5, letterSpacing: '0.5px' }}
          >
            Amenities
          </div>
          <div className="flex flex-wrap gap-1.5">
            {amenities.map((a, i) => (
              <span
                key={i}
                className="flex items-center gap-1 px-2.5 py-1 rounded-full font-medium"
                style={{ background: '#fff', color: 'var(--g700)', fontSize: 12, border: '1px solid var(--g200)' }}
              >
                <Icon name="check-circle" size={12} color="var(--brand)" /> {a}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const AgentInquiries: React.FC = () => {
  const { toast } = useToast();
  const [filter, setFilter] = useState<Filter>('all');
  const [inquiries, setInquiries] = useState<Inquiry[]>([]);
  const [counts, setCounts] = useState<InquiryStatusCounts>({
    all: 0,
    new: 0,
    contacted: 0,
    converted: 0,
  });
  const [selected, setSelected] = useState<number | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  // Full listing for the selected inquiry, loaded inline in the detail panel —
  // the inquiry row only carries a thin listing summary.
  const [panelListing, setPanelListing] = useState<Listing | null>(null);
  const [panelListingLoading, setPanelListingLoading] = useState<boolean>(false);
  // Below lg the list and the detail panel don't fit side by side, so tapping
  // an inquiry swaps the list for its detail (with a back button).
  const [mobileDetail, setMobileDetail] = useState<boolean>(false);
  // Listing photo shown in the on-page viewer.
  const [viewing, setViewing] = useState<{ src: string; title: string } | null>(null);

  const openInquiry = (id: number) => {
    setSelected(id);
    setMobileDetail(true);
    if (window.matchMedia('(max-width: 1023px)').matches) {
      window.scrollTo({ top: 0 });
    }
  };

  const changeFilter = (f: Filter) => {
    setFilter(f);
    setMobileDetail(false);
  };

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await listInquiries({
        status: filter === 'all' ? undefined : filter,
        limit: 100,
      });
      setInquiries(res.inquiries);
      setCounts(res.counts);
      // Auto-select first row when list changes.
      setSelected((prev) => {
        if (prev && res.inquiries.some((i) => i.id === prev)) return prev;
        return res.inquiries.length > 0 ? res.inquiries[0].id : null;
      });
    } catch (err) {
      setLoadError(extractApiError(err, 'Failed to load inquiries'));
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => inquiries, [inquiries]);
  const current = useMemo(
    () => inquiries.find((i) => i.id === selected) || null,
    [inquiries, selected]
  );
  const newCount = counts.new;

  // The open inquiry dropped out of the list (e.g. status changed under a
  // filter) — fall back to the list on phones instead of an empty panel.
  useEffect(() => {
    if (mobileDetail && !loading && !current) setMobileDetail(false);
  }, [mobileDetail, loading, current]);

  // Load the full listing for whichever inquiry is selected, so its details can
  // be shown inline in the panel.
  const currentListingId = current?.listingId ?? null;
  useEffect(() => {
    if (currentListingId === null) {
      setPanelListing(null);
      return;
    }
    let cancelled = false;
    setPanelListing(null);
    setPanelListingLoading(true);
    getListing(currentListingId)
      .then((l) => {
        if (!cancelled) setPanelListing(l);
      })
      .catch(() => {
        // Non-fatal: the thin listing summary in the header still shows.
        if (!cancelled) setPanelListing(null);
      })
      .finally(() => {
        if (!cancelled) setPanelListingLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [currentListingId]);

  const applyStatus = async (l: Inquiry, next: InquiryStatus) => {
    setBusyId(l.id);
    const prevStatus = l.status;
    setInquiries((prev) =>
      prev.map((x) => (x.id === l.id ? { ...x, status: next } : x))
    );
    try {
      const updated = await setInquiryStatus(l.id, next);
      setInquiries((prev) =>
        prev.map((x) => (x.id === l.id ? { ...x, ...updated } : x))
      );
      load();
      toast(`${l.customerName} marked ${next}`, next === 'converted' ? 'success' : 'info');
    } catch (err) {
      setInquiries((prev) =>
        prev.map((x) => (x.id === l.id ? { ...x, status: prevStatus } : x))
      );
      toast(extractApiError(err, 'Failed to update inquiry'), 'error');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div>
      {/* Filter row */}
      <div
        className={
          (mobileDetail ? 'hidden lg:flex' : 'flex') +
          ' gap-1.5 mb-4 sm:mb-5 overflow-x-auto no-scrollbar sm:flex-wrap sm:overflow-visible'
        }
      >
        {FILTERS.map((f) => {
          const active = filter === f;
          const count = counts[f];
          return (
            <button
              key={f}
              className="flex-shrink-0 whitespace-nowrap"
              onClick={() => changeFilter(f)}
              style={{
                padding: '7px 16px',
                borderRadius: 8,
                fontSize: 13,
                fontWeight: active ? 600 : 500,
                border: '1px solid ' + (active ? 'var(--brand)' : 'var(--g200)'),
                background: active ? 'var(--brand-light)' : '#fff',
                color: active ? 'var(--brand)' : 'var(--g600)',
                cursor: 'pointer',
                transition: 'all .15s ease',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              {FILTER_LABEL[f]}
              <span
                style={{
                  fontSize: 10.5,
                  padding: '1px 7px',
                  borderRadius: 100,
                  background: active ? 'var(--brand)' : 'var(--g100)',
                  color: active ? '#fff' : 'var(--g500)',
                  fontWeight: 700,
                }}
              >
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {loadError && (
        <div
          style={{
            background: '#fff5f5',
            border: '1px solid #fed7d7',
            color: '#c53030',
            padding: '10px 14px',
            borderRadius: 10,
            marginBottom: 16,
            fontSize: 14,
          }}
        >
          {loadError}
        </div>
      )}

      {/* Two-column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.3fr] gap-5">
        {/* Inquiry list */}
        <div
          className={'card min-w-0' + (mobileDetail ? ' hidden lg:block' : '')}
          style={{ padding: 0, borderRadius: 16, overflow: 'hidden' }}
        >
          <div
            className="flex justify-between items-center px-5 py-4"
            style={{ borderBottom: '1px solid var(--g100)' }}
          >
            <h3 className="font-bold text-ink" style={{ fontSize: 15 }}>
              {loading ? 'Loading…' : `${filtered.length} ${filter === 'all' ? 'inquiries' : FILTER_LABEL[filter].toLowerCase()}`}
            </h3>
            {newCount > 0 && filter === 'all' && (
              <span
                className="font-bold"
                style={{
                  background: 'var(--brand-bg)',
                  color: 'var(--brand)',
                  fontSize: 11,
                  padding: '3px 10px',
                  borderRadius: 100,
                }}
              >
                {newCount} new
              </span>
            )}
          </div>
          <div className="lg:max-h-[600px] lg:overflow-y-auto">
            {loading && (
              <div className="text-center py-12 text-g500" style={{ fontSize: 13 }}>
                Loading inquiries…
              </div>
            )}
            {!loading && filtered.map((iq) => {
              const active = selected === iq.id;
              return (
                <div
                  key={iq.id}
                  onClick={() => openInquiry(iq.id)}
                  className="flex items-start gap-3 cursor-pointer px-4 py-3.5 sm:px-[18px]"
                  style={{
                    borderBottom: '1px solid var(--g100)',
                    background: active ? 'var(--brand-light)' : 'transparent',
                    borderLeft:
                      '3px solid ' + (active ? 'var(--brand)' : 'transparent'),
                    transition: 'background .15s',
                  }}
                >
                  <div
                    className="rounded-full flex items-center justify-center font-bold flex-shrink-0"
                    style={{
                      width: 38,
                      height: 38,
                      background: 'var(--brand-bg)',
                      color: 'var(--brand)',
                      fontSize: 13,
                    }}
                  >
                    {initials(iq.customerName)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-start gap-2 mb-1">
                      <div
                        className="font-semibold text-ink truncate"
                        style={{ fontSize: 13.5 }}
                      >
                        {iq.customerName}
                      </div>
                      <span
                        className="font-semibold whitespace-nowrap"
                        style={{
                          fontSize: 10.5,
                          padding: '2px 8px',
                          borderRadius: 100,
                          letterSpacing: '0.2px',
                          ...statusStyle(iq.status),
                        }}
                      >
                        {STATUS_LABEL[iq.status]}
                      </span>
                    </div>
                    <div
                      className="text-g500 truncate"
                      style={{ fontSize: 12, marginBottom: 3 }}
                    >
                      {propertyOf(iq)}
                    </div>
                    <div className="text-g400" style={{ fontSize: 11.5 }}>
                      {formatDate(iq.createdAt)} · {formatTime(iq.createdAt)}
                    </div>
                  </div>
                </div>
              );
            })}
            {!loading && filtered.length === 0 && (
              <div className="text-center py-12 text-g500" style={{ fontSize: 13 }}>
                No {filter === 'all' ? '' : FILTER_LABEL[filter].toLowerCase() + ' '}inquiries
              </div>
            )}
          </div>
        </div>

        {/* Detail panel */}
        <div
          className={'card min-w-0' + (mobileDetail ? '' : ' hidden lg:block')}
          style={{ padding: 0, borderRadius: 16 }}
        >
          <button
            type="button"
            className="lg:hidden flex items-center gap-1.5 font-semibold text-g600 px-4 pt-4"
            style={{ fontSize: 13.5 }}
            onClick={() => setMobileDetail(false)}
          >
            <Icon name="arrow-left" size={16} /> Back to inquiries
          </button>
          {!current ? (
            <div className="flex flex-col items-center justify-center text-center" style={{ padding: '80px 32px' }}>
              <div
                className="rounded-[20px] flex items-center justify-center mb-4"
                style={{ width: 72, height: 72, background: 'var(--g100)' }}
              >
                <Icon name="message-square" size={32} color="var(--g400)" />
              </div>
              <h3 className="font-bold text-ink mb-1" style={{ fontSize: 17 }}>
                Select an inquiry
              </h3>
              <p className="text-g500" style={{ fontSize: 14 }}>
                Pick an inquiry from the list to view details.
              </p>
            </div>
          ) : (
            <>
              {/* Header */}
              <div
                className="flex items-center gap-3 px-4 py-4 sm:px-6 sm:py-5"
                style={{ borderBottom: '1px solid var(--g100)' }}
              >
                <div
                  className="rounded-full flex items-center justify-center font-bold flex-shrink-0 w-11 h-11 text-base sm:w-[52px] sm:h-[52px] sm:text-lg"
                  style={{
                    background: 'var(--brand-bg)',
                    color: 'var(--brand)',
                  }}
                >
                  {initials(current.customerName)}
                </div>
                <div className="flex-1 min-w-0">
                  <h2
                    className="font-extrabold text-ink break-words text-[17px] sm:text-[19px]"
                    style={{ letterSpacing: '-0.3px' }}
                  >
                    {current.customerName}
                  </h2>
                  <div className="text-g500 break-words" style={{ fontSize: 13 }}>
                    Inquired about {propertyOf(current)}
                  </div>
                </div>
                <span
                  className="font-semibold whitespace-nowrap flex-shrink-0"
                  style={{
                    fontSize: 11.5,
                    padding: '4px 12px',
                    borderRadius: 100,
                    ...statusStyle(current.status),
                  }}
                >
                  {STATUS_LABEL[current.status]}
                </span>
              </div>

              {/* Body */}
              <div className="px-4 py-4 sm:px-6 sm:py-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
                  <div>
                    <div
                      className="font-semibold text-g400 uppercase mb-1"
                      style={{ fontSize: 11, letterSpacing: '0.6px' }}
                    >
                      Email
                    </div>
                    <a
                      href={'mailto:' + current.email}
                      className="font-medium hover:underline break-all"
                      style={{ fontSize: 13.5, color: 'var(--brand)' }}
                    >
                      {current.email}
                    </a>
                  </div>
                  <div>
                    <div
                      className="font-semibold text-g400 uppercase mb-1"
                      style={{ fontSize: 11, letterSpacing: '0.6px' }}
                    >
                      Phone
                    </div>
                    {current.phone ? (
                      <a
                        href={'tel:' + current.phone}
                        className="font-medium hover:underline"
                        style={{ fontSize: 13.5, color: 'var(--brand)' }}
                      >
                        {current.phone}
                      </a>
                    ) : (
                      <div className="text-g400 italic" style={{ fontSize: 13.5 }}>
                        Not provided
                      </div>
                    )}
                  </div>
                  <div>
                    <div
                      className="font-semibold text-g400 uppercase mb-1"
                      style={{ fontSize: 11, letterSpacing: '0.6px' }}
                    >
                      Submitted
                    </div>
                    <div className="text-ink" style={{ fontSize: 13.5 }}>
                      {formatDate(current.createdAt)} · {formatTime(current.createdAt)}
                    </div>
                  </div>
                  <div>
                    <div
                      className="font-semibold text-g400 uppercase mb-1"
                      style={{ fontSize: 11, letterSpacing: '0.6px' }}
                    >
                      Property
                    </div>
                    <div className="text-ink" style={{ fontSize: 13.5 }}>
                      {propertyOf(current)}
                    </div>
                    {current.listing?.address && (
                      <div className="text-g500" style={{ fontSize: 12, marginTop: 2 }}>
                        {current.listing.address}
                      </div>
                    )}
                  </div>
                </div>

                <div className="mb-5">
                  <div
                    className="font-semibold text-g400 uppercase mb-2"
                    style={{ fontSize: 11, letterSpacing: '0.6px' }}
                  >
                    Message
                  </div>
                  <div
                    className="leading-relaxed text-g700"
                    style={{
                      fontSize: 13.5,
                      padding: '12px 14px',
                      borderRadius: 10,
                      background: 'var(--g50)',
                      border: '1px solid var(--g100)',
                      minHeight: 72,
                    }}
                  >
                    {current.message || (
                      <span className="italic text-g400">
                        No message left by the customer.
                      </span>
                    )}
                  </div>
                </div>

                {/* Full listing this inquiry is about, loaded inline. */}
                <div className="mb-5">
                  <div
                    className="font-semibold text-g400 uppercase mb-2"
                    style={{ fontSize: 11, letterSpacing: '0.6px' }}
                  >
                    Listing Details
                  </div>
                  <ListingDetailsInline
                    listing={panelListing}
                    loading={panelListingLoading}
                    fallbackTitle={propertyOf(current)}
                    onOpenPhoto={(src, title) => setViewing({ src, title })}
                  />
                </div>

                <div className="flex gap-2.5 flex-wrap">
                  <a
                    href={'mailto:' + current.email}
                    className="btn btn-primary flex-1 justify-center sm:flex-none whitespace-nowrap"
                  >
                    <Icon name="mail" size={14} color="#fff" /> Reply by Email
                  </a>
                  {current.phone && (
                    <a
                      href={'tel:' + current.phone}
                      className="btn btn-secondary flex-1 justify-center sm:flex-none whitespace-nowrap"
                    >
                      <Icon name="phone" size={14} /> Call
                    </a>
                  )}
                  {current.status === 'new' && (
                    <button
                      className="btn btn-secondary flex-1 justify-center sm:flex-none whitespace-nowrap"
                      disabled={busyId === current.id}
                      onClick={() => applyStatus(current, 'contacted')}
                    >
                      <Icon name="check" size={14} /> Mark Contacted
                    </button>
                  )}
                  {current.status === 'contacted' && (
                    <button
                      className="btn btn-secondary flex-1 justify-center sm:flex-none whitespace-nowrap"
                      disabled={busyId === current.id}
                      onClick={() => applyStatus(current, 'converted')}
                    >
                      <Icon name="check" size={14} /> Mark Converted
                    </button>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <ImageLightbox
        src={viewing ? viewing.src : null}
        onClose={() => setViewing(null)}
        title={viewing ? viewing.title : 'Photo'}
      />
    </div>
  );
};

export default AgentInquiries;
